import { useMemo } from 'react';
import { ChannelType } from './useMessageValidation';
import { computeMessageBytes } from './useMessageLength';

export interface RecipientLike {
  id: number;
  phone_number: string;
  kakao_channel_linked?: boolean; // friend talk eligibility
  [k: string]: any;
}

export interface PreviewStats {
  total: number;
  alimtalkEligible: number; // assuming current selection channel AT and template ok (placeholder)
  friendtalkEligible: number;
  smsFallback: number; // recipients that would fall back to SMS
  bytes: number;
  messageType: 'SMS' | 'LMS' | 'OVER';
  cost: { AT?: number; FT?: number; SMS?: number; LMS?: number; totalEstimated?: number };
  warnings: string[];
}

interface UsePreviewStatsParams {
  channel: ChannelType;
  recipients: RecipientLike[];
  message: string;
  templateProvided: boolean; // only relevant if channel AT
  costConfig?: { AT?: number; FT?: number; SMS?: number; LMS?: number }; // per-unit cost placeholder
}

export function usePreviewStats({ channel, recipients, message, templateProvided, costConfig = { AT: 0, FT: 0, SMS: 0, LMS: 0 } }: UsePreviewStatsParams): PreviewStats {
  return useMemo(() => {
    const total = recipients.length;
    const bytes = computeMessageBytes(message || '');
    let messageType: 'SMS' | 'LMS' | 'OVER';
    if (bytes <= 90) messageType = 'SMS'; else if (bytes <= 2000) messageType = 'LMS'; else messageType = 'OVER';

    // Eligibility logic:
    // - FriendTalk eligible: kakao_channel_linked === true
    // - AlimTalk eligible as placeholder: require templateProvided (real backend template validation already done elsewhere)
    const friendtalkEligible = recipients.filter(r => r.kakao_channel_linked).length;
    const alimtalkEligible = templateProvided && channel === 'AT' ? total : 0; // simplistic; could adjust per-recipient in future

    // SMS fallback: if channel is AT or FT and recipient not eligible (no channel link or no template) fallback counts.
    let smsFallback = 0;
    if (channel === 'FT') {
      smsFallback = total - friendtalkEligible;
    } else if (channel === 'AT') {
      smsFallback = templateProvided ? 0 : total; // if no template, all fallback
    } else {
      smsFallback = 0; // already SMS/LMS
    }

    const warnings: string[] = [];
    if (messageType === 'OVER') warnings.push('메시지 길이 2000바이트 초과 (전송 불가)');
    if (channel === 'FT' && friendtalkEligible === 0) warnings.push('친구톡 전송 불가: 연동 채널 없음 (전부 SMS 대체 예상)');
    if (channel === 'AT' && !templateProvided) warnings.push('알림톡 템플릿 미선택: SMS 대체 예상');

    // Cost estimate (placeholder): apply per-channel cost for those eligible; fallback counted as SMS/LMS based on length.
    const cost: PreviewStats['cost'] = {};
    const perType = (mt: 'SMS' | 'LMS') => (costConfig[mt] || 0);
    if (channel === 'AT') {
      cost.AT = alimtalkEligible * (costConfig.AT || 0);
      if (smsFallback) {
        if (messageType === 'SMS' || messageType === 'LMS') {
          cost[messageType] = smsFallback * perType(messageType);
        }
      }
    } else if (channel === 'FT') {
      cost.FT = friendtalkEligible * (costConfig.FT || 0);
      if (smsFallback) {
        if (messageType === 'SMS' || messageType === 'LMS') {
          cost[messageType] = smsFallback * perType(messageType);
        }
      }
    } else { // SMS/LMS direct
      if (messageType === 'SMS' || messageType === 'LMS') {
        cost[messageType] = total * perType(messageType);
      }
    }
    cost.totalEstimated = Object.values(cost).reduce((a,b) => a + (typeof b === 'number' ? b : 0), 0);

    return {
      total,
      alimtalkEligible,
      friendtalkEligible,
      smsFallback,
      bytes,
      messageType,
      cost,
      warnings,
    };
  }, [channel, recipients, message, templateProvided, costConfig]);
}

export default usePreviewStats;
